import styled from '@emotion/styled'
import { ApiDeposit, ApiLoan } from 'api/rtk-api'
import ToastNotification from 'components/core-v2/atoms/toast-notification/toastNotification'

import {
  FormItem,
  Label,
  Select,
  Space,
  TextInput,
  ValidationMessage
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import { Errors } from '../../tradebuilder-v2/util'
import { convertToPercentDisplay, round } from '../../tradebuilder-v2/util/round'
import { EditCashInstrumentsActions } from '../state/edit-cash-instruments-slice'

const StyledCashInstrumentNavigation = styled.div`
  background: var(--White99);
  border-bottom: Solid 1px var(--White90);
`

const StyledCashInstrumentContents = styled.div`
  height: calc(100vh - 211px);
  overflow-y: auto;
  input:read-only,
  input:disabled,
  select:disabled {
    background: var(--Transparent);
    color: var(--White40);
    cursor: default;
  }
`

const StyledSelectSmall = styled.div`
  select {
    max-width: 50%;
    min-width: 170px;
    width: 170px;
  }
`

const StyledSelectMicro = styled.div`
  select {
    max-width: 20%;
    min-width: 60px;
    width: 60px;
  }
`

export type CashInstrumentDetailsPanelProps = {
  actions: EditCashInstrumentsActions
  availableCurrencies: string[]
  availableRefRates: string[]
  canProceed: boolean
  errors: Errors
  isNew: boolean
  saveSuccess: boolean
  selectedInstrument?: ApiDeposit | ApiLoan
}

export const CashInstrumentDetailsPanel = ({
  actions,
  availableCurrencies,
  availableRefRates,
  canProceed,
  errors,
  isNew,
  saveSuccess,
  selectedInstrument
}: CashInstrumentDetailsPanelProps) => {
  const inst = selectedInstrument as (ApiDeposit | ApiLoan) & {
    priceScalingFactor?: number
    referenceRate?: string
    referenceRateSpread?: number
  }
  const spreadSign = inst?.referenceRateSpread < 0 ? '-' : '+'
  const absSpread =
    inst?.referenceRateSpread < 0
      ? (inst?.referenceRateSpread || 0) * -1
      : inst?.referenceRateSpread
  const availableSpreads = Array(100)
    .fill(0)
    .map((i, idx) => round(idx / 2000, 4))

  if (inst) {
    return (
      <>
        <StyledCashInstrumentNavigation>
          <Space fontSize="15px" fontWeight="800" mx={5} py={6}>
            {inst.name}&nbsp;
          </Space>
        </StyledCashInstrumentNavigation>
        <StyledCashInstrumentContents>
          <Space px={5} py={6}>
            <FormItem>
              <Label>Instrument ID</Label>
              <TextInput
                readOnly={!isNew}
                value={inst.instrumentId}
                onChange={v => actions.setId(v.target.value)}
              />
              <ValidationMessage errors={errors} fieldName="id" />
            </FormItem>
            <FormItem>
              <Label>Instrument name</Label>
              <TextInput value={inst.name} onChange={v => actions.setName(v.target.value)} />
              <ValidationMessage errors={errors} fieldName="instrument_name" />
            </FormItem>
            <FormItem>
              <Label>Currency</Label>
              <StyledSelectSmall>
                <Select
                  disabled={!isNew}
                  value={inst.currencyIso}
                  onChange={v => actions.setCurrency(v.target.value)}
                >
                  {availableCurrencies.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </StyledSelectSmall>
            </FormItem>
            <FormItem>
              <Label>Reference rate</Label>
              <StyledSelectSmall>
                <Select
                  value={inst.referenceRate}
                  onChange={v => actions.setRefRate(v.target.value)}
                >
                  {availableRefRates.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </StyledSelectSmall>
            </FormItem>
            <FormItem pr={3}>
              <Label>Reference rate spread</Label>
              <Space isflex>
                <Space mr={3}>
                  <StyledSelectMicro>
                    <Select
                      value={spreadSign}
                      onChange={v =>
                        actions.setRefRateSpread({
                          sign: v.target.value,
                          spread: absSpread
                        })
                      }
                    >
                      <option value="+">+</option>
                      <option value="-">-</option>
                    </Select>
                  </StyledSelectMicro>
                </Space>
                <Space ml={3}>
                  <StyledSelectSmall>
                    <Select
                      value={absSpread}
                      onChange={v =>
                        actions.setRefRateSpread({
                          sign: spreadSign,
                          spread: Number(v.target.value)
                        })
                      }
                    >
                      {availableSpreads.map(c => (
                        <option key={c} value={c}>
                          {convertToPercentDisplay(c)}
                        </option>
                      ))}
                    </Select>
                  </StyledSelectSmall>
                </Space>
              </Space>
            </FormItem>
            <FormItem>
              <Label>Price scaling factor</Label>
              <StyledSelectSmall>
                <Select
                  disabled={!isNew}
                  value={inst.priceScalingFactor}
                  onChange={v => actions.setPriceScalingFactor(Number(v.target.value))}
                >
                  <option value={1}>1</option>
                  <option value={100}>100</option>
                </Select>
              </StyledSelectSmall>
            </FormItem>
            <Space isflex alignItems="center">
              <Space mr={5}>
                <IconButton
                  disabled={!canProceed}
                  icon={<Icon name="save" size="default" />}
                  iconPosition="left"
                  operationType="progress"
                  text="Save"
                  onClick={() => actions.saveInstrument()}
                />
              </Space>
              <ToastNotification
                description={`${inst.name} saved successfully!`}
                notificationType="success"
                open={saveSuccess}
                title="Portfolio saved"
                onClose={() => actions.setSaveSuccessFalse()}
              />
            </Space>
          </Space>
        </StyledCashInstrumentContents>
      </>
    )
  }
  return null
}
